import {
  identifyEnvironment as identifyJsQEnvironment,
  environments as utilsEnvironments,
} from "@jsq/utils";
import { environments, type Environments, apiTokens, apiUrls } from "../constants";
import type { JsqFlagsGlobalThis } from "../types";

declare let globalThis: JsqFlagsGlobalThis;

export type EnvironmentInfo = {
  environmentType: Environments;
  environmentKey?: string;
  isCommunal: boolean;
};

type ParsedEnvironment = {
  environmentType: Environments;
  environmentKey?: string;
  isCommunal: boolean;
};

export type ApiInfo = {
  apiToken?: string;
  apiUrl?: string;
};

export function identifyEnvironment(): EnvironmentInfo & ApiInfo {
  const isMobileEnv = !!globalThis.__jsqFlagsMobile__;
  const {
    environmentType,
    environmentKey: parsedKey,
    isCommunal,
  } = isMobileEnv ? parseMobileEnvironment() : parseWebEnvironment();

  // QA shares the production Unleash flag context, so its key is always "production".
  const environmentKey =
    environmentType === environments.qa ? environments.production : parsedKey;

  const { apiToken, apiUrl } = getApiSettings(environmentType);
  // Preview-only override. Lets an IPE point its
  // flags client at an unleash preview (e.g. pruls###) instead of the shared
  // test instance. Ignored in all other environments to prevent prod footguns.
  const override =
    environmentType === environments.preview ? globalThis.__jsqFlagsOverride__ : undefined;
  return {
    environmentType,
    environmentKey,
    isCommunal,
    apiToken: override?.apiToken ?? apiToken,
    apiUrl: override?.apiUrl ?? apiUrl,
  };
}

function parseMobileEnvironment(): ParsedEnvironment {
  const { __jsqFlagsMobile__ } = globalThis;
  switch (__jsqFlagsMobile__) {
    case "local":
    case "qa":
      return {
        environmentType: environments.cit,
        isCommunal: false,
      };
    case "test":
      return {
        environmentType: environments.test,
        isCommunal: false,
      };
    case "production":
      return {
        environmentType: environments.production,
        isCommunal: false,
      };
    default:
      return {
        environmentType: environments.unknown,
        isCommunal: false,
      };
  }
}

function parseWebEnvironment(): ParsedEnvironment {
  const env = identifyJsQEnvironment();
  const { environmentType, environmentKey } = env;

  // treat communal environment as preview
  if (environmentType === utilsEnvironments.communal) {
    return {
      environmentType: environments.preview,
      environmentKey: location.hostname.split(".").at(-3),
      isCommunal: true,
    };
  }

  return {
    environmentType,
    environmentKey,
    isCommunal: false,
  };
}

function getApiSettings(environmentType: Environments): ApiInfo {
  if (environmentType === environments.development && process.env.UNLEASH_LOCAL === "1") {
    return {
      apiToken: apiTokens.development,
      apiUrl: apiUrls.development,
    };
  }

  switch (environmentType) {
    case environments.test:
    case environments.staging:
    case environments.preview:
    case environments.perf:
    case environments.development: // if UNLEASH_LOCAL env var is not set, point dev to test instance
      return {
        apiToken: apiTokens.test,
        apiUrl: apiUrls.test,
      };

    case environments.cit:
      return {
        apiToken: apiTokens.cit,
        apiUrl: apiUrls.cit,
      };
    case environments.qa:
    case environments.production:
      return {
        apiToken: apiTokens.production,
        apiUrl: apiUrls.production,
      };
    case environments.prodops:
      return {
        apiToken: apiTokens.prodops,
        apiUrl: apiUrls.prodops,
      };
    case environments.evals:
      return {
        apiToken: apiTokens.evals,
        apiUrl: apiUrls.evals,
      };
    case environments.circleCI:
      return {
        apiToken: apiTokens.circleCI,
        apiUrl: apiUrls.circleCI,
      };
    default:
      return {
        apiToken: undefined,
        apiUrl: undefined,
      };
  }
}
